import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import StickerFairyLogo from '../assets/img/StickerFairyLogo.jpg';
import { GET_BASE_URL, GET_BASE_URL_IMAGE } from '../constants/apiEndpoints';

export const STICKER_FAIRY_DEAL_CHECKOUT_BASE_URL =
  'https://pay.rev.cat/kiabgvszhgwftvfi';
export const STICKER_FAIRY_DEAL_PATH = '/disneyland/sticker-fairy-deal';

export const buildStickerFairyDealCheckoutUrl = (userId: string) =>
  `${STICKER_FAIRY_DEAL_CHECKOUT_BASE_URL}/${encodeURIComponent(userId)}`;

type GiftMode = 'self' | 'gift';

type MouseWaitUserSuggestion = {
  id: number | string;
  value: string;
  image?: string | null;
};

const dealItems = [
  'Sticker Fairy access',
  '10,000 MouseWait credits',
  'ALL-ACCESS',
];

const StickerFairyDeal = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id') || '';
  const [giftMode, setGiftMode] = useState<GiftMode>('self');
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientOptions, setRecipientOptions] = useState<MouseWaitUserSuggestion[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<MouseWaitUserSuggestion | null>(null);
  const [recipientSearchStatus, setRecipientSearchStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [postPurchaseToLounge, setPostPurchaseToLounge] = useState(false);
  const [isPreparingCheckout, setIsPreparingCheckout] = useState(false);

  const checkoutUserId = giftMode === 'gift' ? String(selectedRecipient?.id || '') : userId;
  const checkoutUrl = checkoutUserId ? buildStickerFairyDealCheckoutUrl(checkoutUserId) : '#';
  const checkoutLabel = useMemo(() => {
    if (giftMode === 'gift') {
      return selectedRecipient ? `Send RAK to ${selectedRecipient.value}` : 'Choose a recipient';
    }

    return userId ? 'Continue to checkout' : 'Log in to buy';
  }, [giftMode, selectedRecipient, userId]);

  useEffect(() => {
    if (giftMode !== 'gift') {
      return;
    }

    const query = recipientQuery.trim();
    if (query.length < 2 || selectedRecipient?.value === query) {
      setRecipientOptions([]);
      setRecipientSearchStatus('idle');
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setRecipientSearchStatus('loading');

      try {
        const response = await fetch(
          `${GET_BASE_URL}/api/v1/getUser?name=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );
        const body = await response.json();
        setRecipientOptions(Array.isArray(body?.data) ? body.data : []);
        setRecipientSearchStatus('idle');
      } catch (error) {
        if (!controller.signal.aborted) {
          setRecipientSearchStatus('error');
          setRecipientOptions([]);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [giftMode, recipientQuery, selectedRecipient]);

  const saveLoungeSharePreference = async () => {
    if (!postPurchaseToLounge || !checkoutUserId) {
      return;
    }

    const authToken = localStorage.getItem('token');
    if (!authToken) {
      return;
    }

    try {
      await fetch(`${GET_BASE_URL}/api/v1/purchase/lounge-share-preference`, {
        method: 'POST',
        headers: {
          Authorization: `bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          purchase_type: 'sticker_fairy_package',
          target_user_id: Number(checkoutUserId),
          post_to_lounge: true,
        }),
      });
    } catch (error) {
      console.error('Could not save Lounge share preference:', error);
    }
  };

  const handleCheckoutClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (giftMode === 'gift') {
      if (!selectedRecipient) {
        event.preventDefault();
        return;
      }
    }

    event.preventDefault();

    if (!checkoutUserId) {
      navigate('/disneyland/login', {
        state: { returnTo: STICKER_FAIRY_DEAL_PATH, source: 'StickerFairyDeal' },
      });
      return;
    }

    setIsPreparingCheckout(true);
    await saveLoungeSharePreference();
    window.location.href = checkoutUrl;
  };

  const handleRecipientQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRecipientQuery(event.target.value);
    setSelectedRecipient(null);
  };

  const selectRecipient = (recipient: MouseWaitUserSuggestion) => {
    setSelectedRecipient(recipient);
    setRecipientQuery(recipient.value);
    setRecipientOptions([]);
  };

  const recipientImageUrl = (recipient: MouseWaitUserSuggestion) => {
    if (!recipient.image) {
      return '';
    }

    return `${GET_BASE_URL_IMAGE}/disneyland/images/thumbs/${recipient.image}`;
  };

  return (
    <>
      <Helmet>
        <title>Sticker Fairy + 10,000 Credits + ALL-ACCESS | MouseWait</title>
        <meta
          name="description"
          content="Get Sticker Fairy, 10,000 MouseWait credits, and ALL-ACCESS in one $99 MouseWait deal."
        />
      </Helmet>

      <main className="mw-sticker-fairy-deal" aria-labelledby="sticker-fairy-deal-title">
        <section className="mw-sticker-fairy-deal__hero">
          <img
            className="mw-sticker-fairy-deal__logo"
            src={StickerFairyLogo}
            alt="Sticker Fairy"
          />
          <div className="mw-sticker-fairy-deal__copy">
            <p className="mw-sticker-fairy-deal__eyebrow">Limited MouseWait deal</p>
            <h1 id="sticker-fairy-deal-title">
              Sticker Fairy + 10,000 Credits + ALL-ACCESS
            </h1>
            <p className="mw-sticker-fairy-deal__lead">
              Get the Sticker Fairy creator, a 10,000 credit boost, and ALL-ACCESS in
              one package for $99.
            </p>
            <div className="mw-sticker-fairy-deal__price" aria-label="Price">
              $99
            </div>
            <div
              className="mw-sticker-fairy-deal__mode"
              role="group"
              aria-label="Who is this package for?"
            >
              <button
                type="button"
                className={giftMode === 'self' ? 'is-active' : ''}
                onClick={() => setGiftMode('self')}
              >
                For me
              </button>
              <button
                type="button"
                className={giftMode === 'gift' ? 'is-active' : ''}
                onClick={() => setGiftMode('gift')}
              >
                RAK
              </button>
            </div>
            <p className="mw-sticker-fairy-deal__mode-note">
              {giftMode === 'gift'
                ? 'RAK means Random Act of Kindness: pick the MouseWaiter who should receive ALL-ACCESS, credits, and Sticker Fairy.'
                : 'The package will be added to your MouseWait account.'}
            </p>
            {giftMode === 'gift' ? (
              <div className="mw-sticker-fairy-deal__gift">
                <label htmlFor="sticker-fairy-gift-recipient">
                  Recipient MouseWait username
                </label>
                <input
                  id="sticker-fairy-gift-recipient"
                  type="search"
                  value={recipientQuery}
                  onChange={handleRecipientQueryChange}
                  placeholder="Start typing a MouseWait username"
                  autoComplete="off"
                />
                {recipientSearchStatus === 'loading' ? (
                  <p className="mw-sticker-fairy-deal__gift-status">Searching...</p>
                ) : null}
                {recipientSearchStatus === 'error' ? (
                  <p className="mw-sticker-fairy-deal__gift-status">
                    Could not search users. Try again.
                  </p>
                ) : null}
                {recipientOptions.length > 0 ? (
                  <div className="mw-sticker-fairy-deal__recipient-list" role="listbox">
                    {recipientOptions.map((recipient) => (
                      <button
                        key={recipient.id}
                        type="button"
                        role="option"
                        aria-selected={selectedRecipient?.id === recipient.id}
                        onClick={() => selectRecipient(recipient)}
                      >
                        {recipient.image ? (
                          <img src={recipientImageUrl(recipient)} alt="" />
                        ) : (
                          <span aria-hidden="true">{recipient.value.slice(0, 1).toUpperCase()}</span>
                        )}
                        <strong>{recipient.value}</strong>
                      </button>
                    ))}
                  </div>
                ) : null}
                {selectedRecipient ? (
                  <p className="mw-sticker-fairy-deal__gift-confirmation">
                    This RAK will go to <strong>{selectedRecipient.value}</strong>.
                  </p>
                ) : null}
              </div>
            ) : null}
            <label className="mw-purchase-share-option mw-sticker-fairy-deal__share">
              <input
                type="checkbox"
                checked={postPurchaseToLounge}
                onChange={(event) => setPostPurchaseToLounge(event.target.checked)}
              />
              <span>
                <strong>Post this purchase to the Lounge</strong>
                <small>
                  Kate (Virtual Plaid) will post a thank-you status after payment.
                </small>
              </span>
            </label>
            <a
              className="mw-sticker-fairy-deal__checkout"
              href={checkoutUrl}
              onClick={handleCheckoutClick}
              aria-disabled={giftMode === 'gift' && !selectedRecipient}
              rel="noopener noreferrer"
            >
              {isPreparingCheckout ? 'Starting checkout...' : checkoutLabel}
            </a>
          </div>
        </section>

        <section className="mw-sticker-fairy-deal__details" aria-label="Deal details">
          <div>
            <h2>Included</h2>
            <ul>
              {dealItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2>After payment</h2>
            <p>
              Your ALL-ACCESS deal turns on automatically. For RAKs, the selected
              MouseWaiter gets the 10,000 credits, Sticker Fairy access, and
              ALL-ACCESS benefits.
            </p>
          </div>
        </section>

        <section className="mw-sticker-fairy-deal__terms" aria-label="Deal terms">
          <h2>Deal terms</h2>
          <p>
            This is a one-time MouseWait purchase. After successful payment, the
            RevenueCat purchase grants the ALL-ACCESS entitlement and adds 10,000
            MouseWait credits to the MouseWait account tied to checkout.
          </p>
        </section>
      </main>
    </>
  );
};

export default StickerFairyDeal;
